import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import styled from 'styled-components';
import {
  AppNotificationAlert,
  AppRemixIcon,
} from '@/layout/AppComponents';
import BreadcrumbHandler from '../../../layout/components/breadcrumb/BreadcrumbHandler';
import useIsMounted from '../../../layout/components/hooks/useIsMounted';
import { axiosPath } from '../../../routing/helpers/router';
import Translator from '../../../layout/components/helpers/Translator';
import * as currentPageAction from '../../../store/actions/currentPageAction';
import EnvironmentalAspectsAssessmentSidebar from '../../../layout/screenSpecifics/Modules/EnvironmentalAspectsAssessment/Sidebar/EnvironmentalAspectsAssessmentSidebar';
import AppSpinner from '../../../layout/components/AppSpinner';
import EnvironmentalAspectsAssessmentAspectContainer from '../../../layout/screenSpecifics/Modules/EnvironmentalAspectsAssessment/EnvironmentalAspectsAssessmentAspectContainer';
import EnvironmentalAspectsAssessmentTopButtons from '../../../layout/screenSpecifics/Modules/EnvironmentalAspectsAssessment/EnvironmentalAspectsAssessmentTopButtons';
import errorHandler from '../../../layout/components/error/ErrorHandler';
import EnvironmentalAspectsAssessmentBase from './EnvironmentalAspectsAssessmentBase';

export default function EnvironmentalAspectsAssessmentInspectionHomeScreen() {
  const { moduleBridgeHex, plantHex, assessmentHex } = useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const moduleData = useSelector(
    (s) => s?.module?.[moduleBridgeHex]?.moduleData
  );
  const inspections = useSelector((s) => s?.currentPage?.inspections);
  const checkedCards = useSelector((s) => s?.currentPage?.checkedCards);
  const [loading, setLoading] = useState(true);
  const [breadcrumb, setBreadcrumb] = useState();
  const [inspectionAssessmentTitle, setInspectionAssessmentTitle] = useState();

  useEffect(() => {
    return () => {
      dispatch(currentPageAction.empty());
    };
  }, []);

  // > LOAD INSPECTION
  const loadInspection = useCallback(
    async () =>
      axios
        .post(
            axiosPath(
              'api_r_user_module_environmentalAspectsAssessment_inspection_get'
            ),
          {
            moduleBridgeHex,
            assessmentHex,
          }
        )
        .then(({ data }) => {
          if (isMounted.current) {
            setInspectionAssessmentTitle(data?.assessmentTitle);
            dispatch(
              currentPageAction.change('inspections', data?.inspections)
            );
            dispatch(
              currentPageAction.change('checkedCards', data?.checkedCards)
            );
          }
        })
        .catch((e) => dispatch(errorHandler(e)))
        .finally(() => setLoading(false)),
    [moduleBridgeHex, plantHex, moduleData]
  );

  useEffect(() => {
    loadInspection();
  }, []);
  // < LOAD INSPECTION

  // > SAVE ASSESSMENT
  const handleSave = async () =>
      axios
        .post(
            axiosPath(
              'api_r_user_module_environmentalAspectsAssessment_inspection_edit'
            ),
          {
            moduleBridgeHex,
            assessmentHex,
            checkedCards,
          }
        )
        .then(() => {
          if (isMounted.current) {
            AppNotificationAlert({
              title: 'Completado',
              message: 'Inspección guardada con éxito.',
              type: 'success',
              duration: 3000,
            });
          }
        })
        .catch((e) => dispatch(errorHandler(e)))
  // < SAVE ASSESSMENT

  useEffect(() => {
    if (moduleData && inspectionAssessmentTitle) {
      const b = [
        { name: moduleData?.moduleName },
        {
          name: 'Fichas de inspecciones',
          route: {
            name: 'user_module_environmentalAspectsAssessment_inspection_list',
            params: {
              moduleBridgeHex,
              plantHex,
            },
          },
        },
        { name: `Inspecciones ${inspectionAssessmentTitle}` },
    ];

      if (moduleData?.plant) {
        b.unshift({
          name: moduleData?.plant.name,
          route: {
            name: 'user_module_legalCompliance_evaluation',
            params: {
              moduleBridgeHex: moduleData?.plant?.moduleBridgeHex,
              plantHex,
            },
          },
        });
      }

      setBreadcrumb(b);
    } else {
      setBreadcrumb([{ name: '...' }]);
    }
  }, [moduleData, inspectionAssessmentTitle]);

  return (
    <>
      <EnvironmentalAspectsAssessmentBase />
      <EnvironmentalAspectsAssessmentSidebar
        activeRoute="user_module_environmentalAspectsAssessment_inspection_list"
        assessmentTitle={moduleData?.assessmentTitle}
      />
      <BreadcrumbHandler paths={breadcrumb} />
      <main role="main" className="col-md-12 col-lg-12 px-md-4 mb-5">
        <div className="justify-content-between flex-wrap flex-md-nowrap align-items-center pt-0 pb-0 mb-0">
          <EnvironmentalAspectsAssessmentTopButtons activeRoute="user_module_environmentalAspectsAssessment_inspection_list" />
          {inspectionAssessmentTitle && (
            <h4 className="my-4">
              Inspecciones <b>{inspectionAssessmentTitle}</b>
            </h4>
          )}
          {inspections?.length > 0 ? (
            <>
              {inspections.map((i) => (
                <React.Fragment key={i.inspectionID}>
                  <EnvironmentalAspectsAssessmentAspectContainer
                    id={i.inspectionID}
                    type="sacyrInspection"
                  />
                </React.Fragment>
              ))}
              <SaveBtn className="btn btn-success btn-sm" onClick={handleSave}>
                Guardar
              </SaveBtn>
            </>
          ) : loading ? (
            <AppSpinner>
              <AppRemixIcon icon="loader-4" size={40} />
            </AppSpinner>
          ) : (
            <div className="alert alert-info">
              No se ha encontrado ninguna inspección
            </div>
          )}
        </div>
      </main>
    </>
  );
}

const SaveBtn = styled.button`
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 100;
`;
